import type { TasklistLearningCenterTask } from '@/types/tasklistJson';

export type TaskCompletionApiRow = { taskId: number; completed: boolean };

/**
 * Backend `taskId` from GET `/tasks` (and PUT `/tasks/complete`): explicit `taskId` in JSON, else numeric `pageId`,
 * else tasklist row `id`.
 */
export function resolveLearningCenterBackendTaskId(task: {
  id: number;
  pageId?: string;
  taskId?: number;
}): number {
  const explicit = task.taskId;
  if (explicit != null && Number.isFinite(Number(explicit))) {
    return Number(explicit);
  }
  const fromPage = Number(String(task.pageId ?? '').trim());
  if (Number.isFinite(fromPage)) return fromPage;
  return Number(task.id);
}

/** Merged row passed to `<LearningCenter tasks={…} />` — structure from JSON, `completed` from API only. */
export type LearningCenterMergedTask = {
  id: number;
  taskId?: number;
  title: string;
  description: string;
  taskType: 'anytime' | 'actionable';
  completed: boolean;
  order: number;
  pageId?: string;
  pathSegment?: string;
  group?: string;
  roles?: string[];
};

/**
 * Backend GET `/tasks?identityId=` (parent rows only — not `type:subtask`): `completed` merged by ids below.
 * - `Number(task.id) === Number(api.taskId)`, or
 * - `Number(task.taskId) === Number(api.taskId)` when set, or
 * - `Number(task.pageId) === Number(api.taskId)` when the backend uses CMS/page ids.
 * API rows that match none of these are ignored; tasks missing from the API are incomplete.
 */
export function mergeLearningCenterTasksWithApi(
  checklistTasks: TasklistLearningCenterTask[],
  apiRows: TaskCompletionApiRow[]
): LearningCenterMergedTask[] {
  const jsonIds = new Set(checklistTasks.map((t) => Number(t.id)));
  const byLocalId = new Map<number, boolean>();

  const localIdForApiTaskId = (apiTaskId: number): number | undefined => {
    if (jsonIds.has(apiTaskId)) return apiTaskId;
    for (const task of checklistTasks) {
      const backend = task.taskId;
      if (backend != null && Number.isFinite(Number(backend)) && Number(backend) === apiTaskId) {
        return Number(task.id);
      }
    }
    for (const task of checklistTasks) {
      const pid = String(task.pageId ?? '').trim();
      if (!pid) continue;
      const n = Number(pid);
      if (Number.isFinite(n) && n === apiTaskId) return Number(task.id);
    }
    return undefined;
  };

  for (const row of apiRows) {
    const tid = Number(row.taskId);
    if (!Number.isFinite(tid)) continue;
    const localId = localIdForApiTaskId(tid);
    if (localId === undefined) continue;
    byLocalId.set(localId, Boolean(row.completed));
  }

  return checklistTasks.map((task) => {
    const id = Number(task.id);
    return {
      id: task.id,
      taskId: task.taskId,
      title: task.title,
      description: task.description,
      taskType: task.taskType,
      order: task.order,
      pageId: task.pageId,
      pathSegment: task.pathSegment,
      group: task.group,
      roles: task.roles,
      completed: byLocalId.has(id) ? Boolean(byLocalId.get(id)) : false,
    };
  });
}
